'use client';

import {
  Button,
  Modal,
  Select,
  Stack,
  TextInput,
  Textarea,
  Group,
  Checkbox,
  Switch,
  ColorInput,
} from '@mantine/core';
import { useState, useEffect } from 'react';
import { Dropzone } from '../../EditorText/Dropzone';
import { sliceDefinitions } from '@/src/types/sliceDefinitions';
import styles from './AddSliceModal.module.scss';

type AddSliceModalProps = {
  onAdd: (slice: Record<string, any>) => void;
  opened: boolean;
  onClose: () => void;
};

export const AddSliceModal = ({ onAdd, opened, onClose }: AddSliceModalProps) => {
  const [type, setType] = useState<string | null>(null);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [arrayItems, setArrayItems] = useState<Record<string, any[]>>({});

  const handleFieldChange = (key: string, value: any) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleArrayItemChange = (
    fieldKey: string,
    index: number,
    itemKey: string,
    value: any
  ) => {
    setArrayItems((prev) => {
      const items = [...(prev[fieldKey] || [])];
      if (!items[index]) items[index] = itemKey ? {} : '';
      if (itemKey) {
        items[index] = { ...(items[index] || {}), [itemKey]: value };
      } else {
        items[index] = value;
      }
      return { ...prev, [fieldKey]: items };
    });
  };

  const addArrayItem = (fieldKey: string) => {
    setArrayItems((prev) => ({
      ...prev,
      [fieldKey]: [...(prev[fieldKey] || []), {}],
    }));
  };

  const handleSubmit = () => {
    if (!type) return;
    // @ts-ignore
    const arrayField = sliceDefinitions[type]?.arrayField;
    const arrayPayload = arrayField
      ? { [arrayField.key]: arrayItems[arrayField.key] || [] }
      : {};
    onAdd({ type, ...formData, ...arrayPayload });
    onClose();
    setFormData({});
    setArrayItems({});
    setType(null);
  };

    // -----------------------------
    // Prefill defaults when type changes
    // -----------------------------
  useEffect(() => {
    if (!type) return;
    const def: any = (sliceDefinitions as any)[type];
    const next: Record<string, any> = {};
    if (def?.fields) {
      for (const f of def.fields) {
        if (Object.prototype.hasOwnProperty.call(f, 'default')) {
          next[f.key] = f.default;
        }
      }
    }
    setFormData(next);
    setArrayItems({});
  }, [type]);

  // @ts-ignore
  const fields = type ? sliceDefinitions[type]?.fields ?? [] : [];
  // @ts-ignore
  const arrayField = type ? sliceDefinitions[type]?.arrayField : undefined;

  const renderField = (field: any, value: any, onChange: (v: any) => void) => {
    if (field.type === 'image') {
      return (
        <Dropzone
          imageUrl={value || ''}
          setImageUrl={(url) => onChange(url)}
        />
      );
    } else if (field.type === 'textarea') {
      return (
        <Textarea
          value={value || ''}
          onChange={(e) => onChange(e.currentTarget.value)}
        />
      );
    } else if (field.type === 'boolean') {
      return (
        <Checkbox
          label={field.label}
          checked={!!value}
          onChange={(e) => onChange(e.currentTarget.checked)}
        />
      );
    } else if (field.key === 'version') {
      return (
        <Switch
          label="Thème sombre"
          checked={value === 'dark'}
          onChange={(e) => onChange(e.currentTarget.checked ? 'dark' : 'light')}
        />
      );
    } else if (field.type === 'color') {
      return (
        <ColorInput
          value={value || ''}
          onChange={onChange}
          withEyeDropper
          disallowInput
          format="hex"
        />
      );
    } else if (field.type === 'position') {
      return (
        <Select
          placeholder="Choisir une position"
          data={[
            { value: 'left', label: 'Gauche' },
            { value: 'right', label: 'Droite' },
          ]}
          value={value || ''}
          onChange={(val) => onChange(val)}
        />
      );
    } else {
      return (
        <TextInput
          value={value || ''}
          onChange={(e) => onChange(e.currentTarget.value)}
        />
      );
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Ajouter un bloc"
      size="lg"
      centered
      className={styles.modal}
    >
      <Stack>
        <Select
          label="Type de slice"
          placeholder="Choisir un type"
          data={Object.entries(sliceDefinitions).map(([value, def]) => ({
            value,
            // @ts-ignore
            label: def.label,
          }))}
          value={type}
          onChange={(val) => setType(val)}
        />

        {/* Force a fresh mount of fields when type changes (keeps UI clean) */}
        <div key={`fields-${type || 'none'}`}>
          {fields.map((field: any) => (
            <div key={field.key} style={{ marginBottom: 12 }}>
              {field.type !== 'boolean' && field.key !== 'theme' && (
                <label style={{ display: 'block', marginBottom: 6 }}>
                  {field.label}
                </label>
              )}
              {renderField(
                field,
                formData[field.key],
                (val) => handleFieldChange(field.key, val)
              )}
            </div>
          ))}
        </div>

        {arrayField && (
          <div>
            <h4>{arrayField.label}</h4>
            {(arrayItems[arrayField.key] || []).map((item, index) => (
              <Stack
                key={index}
                style={{
                  border: '1px solid #ccc',
                  padding: '1rem',
                  marginBottom: '1rem',
                }}
              >
                {arrayField.fields ? (
                  arrayField.fields.map((field: any) => (
                    <div key={field.key}>
                      <label style={{ display: 'block', marginBottom: 6 }}>
                        {field.label}
                      </label>
                      {renderField(
                        field,
                        item[field.key],
                        (val) =>
                          handleArrayItemChange(
                            arrayField.key,
                            index,
                            field.key,
                            val
                          )
                      )}
                    </div>
                  ))
                ) : (
                  <div>
                    <label style={{ display: 'block', marginBottom: 6 }}>
                      Image
                    </label>
                    {renderField(
                      { type: 'image' },
                      item,
                      (val) =>
                        handleArrayItemChange(arrayField.key, index, '', val)
                    )}
                  </div>
                )}
              </Stack>
            ))}

            <Button
              size="xs"
              variant="outline"
              onClick={() => addArrayItem(arrayField.key)}
            >
              ➕ Ajouter un élément
            </Button>
          </div>
        )}

        <Group justify="end">
          <Button onClick={handleSubmit} disabled={!type}>
            Ajouter
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
